import type { RichTextBlock, RichTextChild, RichTextLink, RichTextListItem } from '@/Locations/types/locations';

interface RichTextRendererProps {
  content: RichTextBlock[] | string | undefined;
  className?: string;
}

function renderTextChild(child: RichTextChild, index: number): React.ReactNode {
  // Handle code blocks that contain HTML - render as raw HTML
  if (child.code && child.text && /<[a-z][\s\S]*>/i.test(child.text)) {
    return (
      <span
        key={index}
        dangerouslySetInnerHTML={{ __html: child.text }}
      />
    );
  }

  let element: React.ReactNode = child.text;

  if (child.code) {
    element = <code key={index} className="bg-muted px-1 py-0.5 rounded text-sm">{element}</code>;
  }
  if (child.bold) {
    element = <strong key={index}>{element}</strong>;
  }
  if (child.italic) {
    element = <em key={index}>{element}</em>;
  }
  if (child.underline) {
    element = <u key={index}>{element}</u>;
  }
  if (child.strikethrough) {
    element = <s key={index}>{element}</s>;
  }

  return element;
}

function renderLink(link: RichTextLink, index: number): React.ReactNode {
  const linkContent = link.children.map((child, idx) => renderTextChild(child, idx));

  return (
    <a
      key={index}
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary hover:underline"
    >
      {linkContent}
    </a>
  );
}

function renderChild(child: RichTextChild | RichTextLink, index: number): React.ReactNode {
  if (child.type === 'link') {
    return renderLink(child as RichTextLink, index);
  }
  return renderTextChild(child as RichTextChild, index);
}

function renderBlock(block: RichTextBlock, index: number): React.ReactNode {
  const blockType = block.type?.toLowerCase();

  switch (blockType) {
    case 'paragraph': {
      // Check if paragraph contains HTML tags
      const hasHtmlTags = block.children.some((child) => {
        if ('text' in child && (child as RichTextChild).text) {
          const text = (child as RichTextChild).text;
          return /<[a-z][\s\S]*>/i.test(text);
        }
        return false;
      });

      if (hasHtmlTags) {
        // Render as HTML if it contains HTML tags
        const htmlContent = block.children
          .map((child) => {
            if ('text' in child && (child as RichTextChild).text) {
              return (child as RichTextChild).text;
            }
            return '';
          })
          .join('');
        return (
          <div
            key={index}
            className="mb-2"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );
      }

      const content = block.children.map((child, idx) => {
        if ('type' in child && (child.type === 'text' || child.type === 'link')) {
          return renderChild(child as RichTextChild | RichTextLink, idx);
        }
        return null;
      });
      return <p key={index} className="mb-2">{content}</p>;
    }

    case 'heading': {
      const content = block.children.map((child, idx) => {
        if ('type' in child && (child.type === 'text' || child.type === 'link')) {
          return renderChild(child as RichTextChild | RichTextLink, idx);
        }
        return null;
      });
      const level = block.level || 3;
      const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
      const headingClasses: Record<number, string> = {
        1: 'text-2xl font-bold text-foreground mb-3',
        2: 'text-xl font-semibold text-foreground mb-2',
        3: 'text-lg font-semibold text-foreground mb-2',
        4: 'text-base font-semibold text-foreground mb-2',
        5: 'text-sm font-semibold text-foreground mb-1',
        6: 'text-sm font-medium text-foreground mb-1',
      };
      return <HeadingTag key={index} className={headingClasses[level] || headingClasses[3]}>{content}</HeadingTag>;
    }

    case 'list': {
      const ListTag = block.format === 'ordered' ? 'ol' : 'ul';
      const listClass = block.format === 'ordered'
        ? 'list-decimal list-inside mb-2 space-y-1'
        : 'list-disc list-inside mb-2 space-y-1';

      return (
        <ListTag key={index} className={listClass}>
          {block.children.map((item, idx) => {
            if ((item as RichTextListItem).type === 'list-item') {
              const listItem = item as RichTextListItem;
              return (
                <li key={idx}>
                  {listItem.children.map((child, childIdx) =>
                    renderChild(child, childIdx)
                  )}
                </li>
              );
            }
            return null;
          })}
        </ListTag>
      );
    }

    case 'quote': {
      const content = block.children.map((child, idx) => {
        if ('type' in child && (child.type === 'text' || child.type === 'link')) {
          return renderChild(child as RichTextChild | RichTextLink, idx);
        }
        return null;
      });
      return (
        <blockquote key={index} className="border-l-4 border-primary pl-4 italic mb-2">
          {content}
        </blockquote>
      );
    }

    case 'code':
    case 'html': {
      // Extract raw HTML from code/html block children
      const htmlContent = block.children
        .map((child) => {
          if ('text' in child && (child as RichTextChild).text) {
            return (child as RichTextChild).text;
          }
          return '';
        })
        .join('');

      // Render HTML content using dangerouslySetInnerHTML
      return (
        <div
          key={index}
          className="mb-2 rich-text-html"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      );
    }

    default:
      return null;
  }
}

export function RichTextRenderer({ content, className = '' }: RichTextRendererProps) {
  // Handle undefined or null
  if (!content) {
    return null;
  }

  // Handle plain string (backwards compatibility)
  if (typeof content === 'string') {
    // Check if string contains HTML tags
    if (/<[a-z][\s\S]*>/i.test(content)) {
      return (
        <div
          className={className}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }
    return <p className={`whitespace-pre-line ${className}`}>{content}</p>;
  }

  // Handle rich text blocks array
  if (Array.isArray(content)) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        {content.map((block, index) => renderBlock(block, index))}
      </div>
    );
  }

  return null;
}
